import { useEffect, useRef, useState } from "react";

/** COMPONENT */
import { Box, Typography } from "@mui/material";
import ModalCustom from "../../../component/modalCustom/modalCustom";
import Loading from "../../../component/loading/loading";
import { notiError, notiSuccess } from "../../../component/notification/notifications";
import DepositContractTable from "../components/DepositContractTable";

/** API */
import DepositRefundApi, { DepositRefundLease } from "../../../api/deposit-refund";

/** UTILS */
import { colors } from "../../../constants/colors";
import { getApiErrorMessage } from "../../../utils/api-error.utils";

/** STYLE */
import * as Style from "../depositRefund.style";

type Props = {
  onClose: () => void;
  /** สำเร็จแล้ว: parent ปิด modal + reload ตาราง ในจังหวะเดียว */
  onSuccess: () => void;
};

export default function ModalDepositRefundCreate({ onClose, onSuccess }: Props) {
  const [leases, setLeases] = useState<DepositRefundLease[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  /** กันกดปุ่มรัวก่อน isSubmitting จะ re-render ทัน — 1 สัญญามีคำร้องค้างได้ใบเดียว */
  const isSubmittingRef = useRef<boolean>(false);

  useEffect(() => {
    /** กัน setState หลัง unmount ตอนผู้ใช้ปิด modal ก่อน response กลับมา */
    let isMounted = true;

    const loadLeases = async () => {
      setIsLoading(true);
      try {
        // ลูกค้าไม่ต้องส่ง userId — backend scope จาก token ให้
        const res = await DepositRefundApi.myLease();
        if (!isMounted) return;
        setLeases(res.data?.data || []);
      } catch (error) {
        if (!isMounted) return;
        setLeases([]);
        notiError(getApiErrorMessage(error, "ดึงรายการสัญญาเช่าไม่สำเร็จ"));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadLeases();
    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * นับเฉพาะสัญญาที่ยื่นได้จริง — สัญญาที่มีคำร้องค้างอยู่ยังแสดงในตาราง (แบบ disabled)
   * เพื่อให้ผู้เช่าเห็นว่าสัญญาไม่ได้หายไปไหน แต่ไม่ควรถูกนับใน "เลือก x จาก N"
   */
  const selectableCount = leases.filter((lease) => lease.can_request).length;

  const onSubmit = async () => {
    if (!selectedId || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      const res = await DepositRefundApi.createRequest({ leaseAgreementId: selectedId });
      notiSuccess(
        `ส่งคำร้องขอคืนค่ามัดจำสำเร็จ ${res.data?.data?.deposit_refund_no || ""}`.trim()
      );
      onSuccess();
    } catch (error) {
      // backend ตอบข้อความไทยมาแล้ว (ไม่พบสัญญา / มีคำร้องค้าง / ไม่มีเงินมัดจำ) แสดงตรงๆ ได้เลย
      notiError(getApiErrorMessage(error, "ส่งคำร้องขอคืนค่ามัดจำไม่สำเร็จ"));
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const renderBody = () => (
    <Box
      className="custom-scroll"
      sx={{ px: { xs: 2, md: 3 }, pb: 1, maxHeight: "calc(100vh - 260px)", overflowY: "auto" }}
    >
      <Typography sx={{ fontSize: 16, color: colors.textLightGray, textAlign: "center", mb: 3 }}>
        เลือกสัญญาเช่าที่ต้องการขอคืนเงินมัดจำ
      </Typography>

      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1, flexWrap: "wrap" }}>
        <Style.SectionTitle>สัญญาเช่าที่มีเงินมัดจำ</Style.SectionTitle>
        {!isLoading && selectableCount > 0 && (
          <Typography sx={{ fontSize: 16, color: colors.textLightGray }}>
            {`เลือก ${selectedId ? 1 : 0} จาก ${selectableCount} สัญญา`}
          </Typography>
        )}
      </Box>

      <DepositContractTable
        leases={leases}
        selectedId={selectedId}
        onSelect={(leaseAgreementId) => setSelectedId(leaseAgreementId)}
        disabled={isSubmitting}
        textEmptyData={
          isLoading ? "กำลังโหลดข้อมูลสัญญาเช่า" : "ไม่พบสัญญาเช่าที่มีเงินมัดจำ"
        }
      />
    </Box>
  );

  return (
    <>
      {isSubmitting && <Loading show />}
      <ModalCustom
        // ต้องเป็น true เสมอ + ให้ parent conditional render (ค่า false จะ fall back ไปอ่าน redux)
        isShowProps
        // ปิด modal เองผ่าน onClose ไม่ให้ไป reset redux modal.isShow ของ modal อื่นในหน้าเดียวกัน
        autoResetModal={false}
        size="xl"
        closeButton
        alignFooter="end"
        footerInline
        title="ส่งคำร้องขอคืนค่ามัดจำแผง"
        component={renderBody()}
        textBtnCancel="ยกเลิก"
        onReset={onClose}
        textBtnConfirm="ส่งคำร้อง"
        onSubmit={onSubmit}
        disabledSubmit={!selectedId || isSubmitting}
        onClose={onClose}
      />
    </>
  );
}
